import { Group } from '@fukuri-admins/apis/client/gen/model';
import { useNavigate } from '@fukuri-admins/hooks/common';
import { useAlertDialog } from '@fukuri-admins/ui/common/AlertDialog';
import { useToast } from '@fukuri-admins/ui/common/Toast/hooks';
import { getThreeLetterCode } from '@fukuri-admins/utils/common/cookies';
import { useRef, useState } from 'react';
import { useGetPartnerAdminsGroups } from '~/hooks/useGetPartnerAdminsGroups';
import {
  ActionTableSortParamsType,
  defaultActionTableSortParams,
} from '../../../types/table/ActionTableSortParamsType';
import type { GroupListPresentation } from '../presentations';
import { useDeletePartnerAdminsGroup } from './useDeletePartnerAdminsGroup';

type PresentationProps = React.ComponentProps<typeof GroupListPresentation>;

type IUseGroupList = () => {
  state: {
    groups: Group[];
    sortParams: ActionTableSortParamsType;
    menuAnchorEl: HTMLElement | null;
  };
  actions: {
    setSortParams: React.Dispatch<React.SetStateAction<ActionTableSortParamsType>>;
    handleClickRow: (groupId: number) => void;
    onClickMenu: (event: React.MouseEvent<HTMLButtonElement>, groupId: number) => void;
    onCloseMenu: PresentationProps['onCloseMenu'];
    onClickEdit: PresentationProps['onClickEdit'];
    onClickDelete: PresentationProps['onClickDelete'];
  };
};

/**
 *
 * @returns state.groups - グループ一覧
 * @returns state.sortParams - ソートパラメータ
 * @returns state.menuAnchorEl - メニューのアンカー要素
 * @returns actions.onSort - ソート処理
 * @returns actions.onClickMenu - メニュークリック処理
 * @returns actions.onCloseMenu - メニュークローズ処理
 * @returns actions.onClickEdit - 編集クリック処理
 * @returns actions.onClickDelete - 削除クリック処理
 */
export const useGroupList: IUseGroupList = () => {
  const navigate = useNavigate();
  const { showAlertDialog } = useAlertDialog();
  const { showToast } = useToast();
  const threeLetterCode = getThreeLetterCode();

  const { data, mutate } = useGetPartnerAdminsGroups({
    threeLetterCode,
  });

  const [sortParams, setSortParams] = useState<ActionTableSortParamsType>(
    defaultActionTableSortParams
  );
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const selectedGroupRef = useRef<Group | null>(null);

  const { trigger: deleteGroup } = useDeletePartnerAdminsGroup(threeLetterCode, {
    onSuccess: () => {
      showToast({
        message: `「${selectedGroupRef.current?.name}」を削除しました。`,
        status: 'success',
      });
      mutate();
    },
    onError: ({ response }) => {
      const errors = response?.data?.errors;
      const errorDetail = errors?.[0] ?? '企業があるため削除できません。';
      showAlertDialog({
        title: '削除できません',
        message: `「${selectedGroupRef.current?.name}」を参照している${errorDetail}`,
        confirmText: '閉じる',
        onConfirm: onCloseMenu,
        showSecondaryButton: false,
      });
    },
  });

  const onClickMenu = (event: React.MouseEvent<HTMLButtonElement>, groupId: number) => {
    setMenuAnchorEl(event.currentTarget);
    const group = data.groups.find(g => g.id === groupId);
    selectedGroupRef.current = group ?? null;
  };

  const onCloseMenu = () => {
    setMenuAnchorEl(null);
  };

  const onClickEdit = () => {
    if (selectedGroupRef.current) {
      navigate(`/groups/${selectedGroupRef.current.id}/edit`);
    }
    onCloseMenu();
  };

  const onClickDelete = () => {
    onCloseMenu();
    if (selectedGroupRef.current) {
      const group = selectedGroupRef.current;
      showAlertDialog({
        title: `「${group.name}」を削除しますか？`,
        message: `「${group.name}」（ID：${group.code}）を削除します。\n削除すると元に戻すことはできません。`,
        confirmText: '削除',
        confirmButtonVariant: 'destructive',
        onConfirm: async () => {
          await deleteGroup(group.id);
        },
      });
    }
  };

  const handleClickRow = (groupId: number) => navigate(`/groups/${groupId}`);

  return {
    state: {
      groups: data.groups,
      sortParams,
      menuAnchorEl,
    },
    actions: {
      setSortParams,
      handleClickRow,
      onClickMenu,
      onCloseMenu,
      onClickEdit,
      onClickDelete,
    },
  };
};
